// Single global axios interceptor — the ONE place where backend errors get
// translated for end users (toast) AND developers (console.error with full
// forensic detail + the X-Request-ID for support tickets).
//
// Imported once from App.js. Every `import axios from "axios"` elsewhere
// goes through this interceptor automatically.
import axios from "axios";
import { toast } from "sonner";

const RID_HDR = "x-request-id";

// Map of common HTTP statuses → user-facing NL messages. Falls back to the
// backend's `error.message` when present (the backend already speaks NL).
const FALLBACK_BY_STATUS = {
  400: "Ongeldige aanvraag. Controleer de invoer en probeer opnieuw.",
  401: "Je sessie is verlopen. Log opnieuw in.",
  403: "Je hebt geen toegang tot deze actie.",
  404: "Niet gevonden.",
  409: "Dit kan nu niet — er is een conflict met de huidige status.",
  422: "Een veld is niet correct ingevuld.",
  429: "Te veel verzoeken. Wacht even en probeer opnieuw.",
  500: "Tijdelijk probleem aan onze kant. Probeer het zo opnieuw.",
  502: "Een externe service is niet bereikbaar. Probeer het zo opnieuw.",
  503: "Tijdelijk niet beschikbaar. Probeer het zo opnieuw.",
};

// Endpoints where we DON'T want to flash a toast (the calling code handles
// the UX itself, e.g. /auth/me returning 401 is normal for logged-out users).
const SILENT_PATHS = [/\/api\/auth\/me$/, /\/api\/leads\/count$/];

function shouldSilence(url = "") {
  return SILENT_PATHS.some((re) => re.test(url));
}

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    const cfg = error.config || {};
    const url = cfg.url || "";
    const status = error.response?.status;
    const data = error.response?.data;
    const requestId =
      error.response?.headers?.[RID_HDR] ||
      data?.error?.request_id ||
      "n/a";

    // Prefer backend's structured envelope; fall back to status map; final
    // fallback is the network-error message.
    const backendMsg = data?.error?.message;
    const message =
      backendMsg ||
      FALLBACK_BY_STATUS[status] ||
      (error.code === "ECONNABORTED"
        ? "De verbinding duurde te lang. Controleer je internet."
        : "Geen verbinding met de server.");

    // Developer forensics — full payload, status, URL, request_id.
    // Goes to console.error so it shows up in browser devtools but is silent
    // for end users.
    // eslint-disable-next-line no-console
    console.error("[api]", cfg.method?.toUpperCase(), url, "→", status ?? "network", {
      request_id: requestId,
      backend_error: data?.error,
      raw: data,
      message: error.message,
    });

    if (!shouldSilence(url)) {
      // 401/403: short toast, no rid (expected: session expired / no access).
      if (status === 401 || status === 403) {
        toast.error(message);
      } else if (requestId !== "n/a") {
        // Server-side fault or validation: show rid + copy button so users
        // can paste it into support emails.
        toast.error(message, {
          description: `Referentie: ${requestId}`,
          duration: 8000,
          action: {
            label: "Kopieer ref.",
            onClick: () => {
              try {
                navigator.clipboard?.writeText(requestId);
                toast.success("Referentie gekopieerd", { duration: 2000 });
              } catch {
                /* clipboard blocked — silent */
              }
            },
          },
        });
      } else {
        // No rid (network error / CORS / timeout) — just the message.
        toast.error(message);
      }
    }

    return Promise.reject(error);
  },
);

export default axios;
